import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/seo/Breadcrumb';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/retos' },
  { hreflang: 'en', href: '/en/challenges' },
  { hreflang: 'x-default', href: '/retos' },
];

const TEXT = {
  es: {
    seoTitle: 'Retos de innovación 2026 | Valoriza',
    seoDescription: 'Ocho retos priorizados por el Comité de Innovación para 2026 y 2027. De outputs de planta sin mercado a la brecha digital de +12.000 operarios.',
    jsonLdName: 'Retos de innovación 2026 de Valoriza',
    breadcrumb: [{ label: 'Inicio', to: '/' }, { label: 'Retos' }],
    tag: 'Retos 2026',
    h1a: 'Los 8 retos que van a ocupar',
    h1b: '2026',
    lead: 'Ocho retos priorizados por el Comité de Innovación para 2026 y 2027. De outputs de planta sin mercado a la brecha digital de +12.000 operarios.',
    howTitle: 'Cómo se seleccionan',
    steps: ['Talleres participativos (+40 personas de la organización)', 'Análisis de 20 retos: matriz de impacto/dificultad, TOP 8', 'Comité de Innovación: TOP 3 y TOP 5, sponsors, vehículos de ejecución', 'Activación H2 2026'],
    top3Title: 'TOP 3 — Foco principal',
    top5Title: 'TOP 5 — Radar activo',
    top3: [
      { name: 'Outputs de planta sin mercado', tag: 'A1 · H2 2026', claim: 'Convertir el residuo en recurso. De coste a ingreso. Nuevas líneas de negocio. Liderazgo en circularidad.' },
      { name: 'Reducción de riesgos físicos críticos', tag: 'A2 · H2 2026', claim: 'Seguridad de las personas. Diferencial en licitación. Menos accidentes.' },
      { name: 'Sensorización de flota y gestor común', tag: 'A2 · H2 2026', claim: 'Eficiencia operativa transversal.' },
    ],
    top5: [
      { name: 'Nuevos flujos de residuo emergentes', tag: 'A1 · 2026–2027', claim: 'Nuevas líneas de negocio.' },
      { name: 'Brecha digital en operaciones', tag: 'A3 · arrastrado 2025', claim: '+12.000 personas a formar. Sin adopción, no hay innovación.' },
      { name: 'Captura y almacenamiento de CO₂', tag: 'A1 · arrastrado 2025', claim: 'Condición para las cadenas energéticas.' },
      { name: 'Inteligencia de mercado sistemática', tag: 'A4 · 2026–2027', claim: 'Anticipar al cliente y ganar ofertas.' },
      { name: 'Batalla social — biometano y valorización energética', tag: 'A1 · 2026–2027', claim: 'Desbloquear proyectos energéticos.' },
    ],
    linksTitle: 'Enlaces',
    linkStrategy: 'Nuestra estrategia de innovación',
    linkProjects: 'Nuestros proyectos financiados',
    cta: '¿Tienes una solución para alguno de estos retos?',
    strategyPath: '/estrategia',
    projectsPath: '/proyectos',
    contactPath: '/contacto',
  },
  en: {
    seoTitle: '2026 Innovation Challenges | Valoriza',
    seoDescription: 'Eight challenges prioritized by the Innovation Committee for 2026 and 2027. From plant outputs with no market to the digital gap affecting +12,000 field workers.',
    jsonLdName: 'Valoriza’s 2026 innovation challenges',
    breadcrumb: [{ label: 'Home', to: '/en' }, { label: 'Challenges' }],
    tag: '2026 Challenges',
    h1a: 'The 8 challenges that will shape',
    h1b: '2026',
    lead: 'Eight challenges prioritized by the Innovation Committee for 2026 and 2027. From plant outputs with no market to the digital gap affecting +12,000 field workers.',
    howTitle: 'How they get selected',
    steps: ['Participatory workshops (+40 people across the organization)', 'Analysis of 20 challenges: impact/difficulty matrix, TOP 8', 'Innovation Committee: TOP 3 and TOP 5, sponsors, execution vehicles', 'H2 2026 activation'],
    top3Title: 'TOP 3 — Main focus',
    top5Title: 'TOP 5 — Active radar',
    top3: [
      { name: 'Plant outputs with no market', tag: 'A1 · H2 2026', claim: 'Turning waste into a resource. From cost to revenue. New business lines. Circularity leadership.' },
      { name: 'Reducing critical physical risks', tag: 'A2 · H2 2026', claim: 'People’s safety. A differentiator in tenders. Fewer accidents.' },
      { name: 'Fleet sensorization and a shared manager', tag: 'A2 · H2 2026', claim: 'Cross-cutting operational efficiency.' },
    ],
    top5: [
      { name: 'New emerging waste streams', tag: 'A1 · 2026–2027', claim: 'New business lines.' },
      { name: 'Digital gap in operations', tag: 'A3 · carried over from 2025', claim: '+12,000 people to train. Without adoption, there’s no innovation.' },
      { name: 'CO₂ capture and storage', tag: 'A1 · carried over from 2025', claim: 'A condition for energy value chains.' },
      { name: 'Systematic market intelligence', tag: 'A4 · 2026–2027', claim: 'Anticipate the client and win bids.' },
      { name: 'Social battle — biomethane and energy recovery', tag: 'A1 · 2026–2027', claim: 'Unlocking energy projects.' },
    ],
    linksTitle: 'Links',
    linkStrategy: 'Our innovation strategy',
    linkProjects: 'Our funded projects',
    cta: 'Do you have a solution for one of these challenges?',
    strategyPath: '/en/strategy',
    projectsPath: '/en/projects',
    contactPath: '/en/contact',
  },
};

function RetoCard({ r, i, big }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: i * 0.08 }}
      className="liquid-glass rounded-2xl p-6"
      data-testid={`reto-card-${i}`}
    >
      <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full inline-flex mb-3" style={{ background: 'rgba(141,194,31,0.12)', color: '#8DC21F' }}>{r.tag}</span>
      <h3 className={`font-bold text-black dark:text-white/90 mb-2 ${big ? 'text-lg' : 'text-base'}`}>{r.name}</h3>
      <p className="text-sm text-gray-700 dark:text-white/65 leading-relaxed">{r.claim}</p>
    </motion.div>
  );
}

export default function RetosPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const t = TEXT[locale];
  const path = locale === 'en' ? '/en/challenges' : '/retos';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: t.jsonLdName,
    url: `https://innovation.valorizasm.com${path}`,
    numberOfItems: 8,
  };

  return (
    <>
      <SEOHead
        title={t.seoTitle}
        description={t.seoDescription}
        path={path}
        locale={locale}
        alternates={ALTERNATES}
        jsonLd={[jsonLd]}
      />
      <div className="pt-32 pb-24 v-container" data-testid="retos-page">
        <Breadcrumb items={t.breadcrumb} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl mb-6">
          <span className="v-tag green mb-6 inline-flex">{t.tag}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-6 tracking-tight leading-tight" data-testid="retos-h1">
            {t.h1a} <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>{t.h1b}</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 leading-relaxed">{t.lead}</p>
        </motion.div>

        <section className="mb-12 max-w-3xl">
          <h2 className="text-lg font-bold text-black dark:text-white/90 mb-3">{t.howTitle}</h2>
          <ol className="space-y-2">
            {t.steps.map((s, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700 dark:text-white/65 leading-relaxed">
                <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: 'rgba(141,194,31,0.12)', color: '#8DC21F' }}>{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-bold text-black dark:text-white/90 mb-5">{t.top3Title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {t.top3.map((r, i) => <RetoCard key={r.name} r={r} i={i} big />)}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-xl font-bold text-black dark:text-white/90 mb-5">{t.top5Title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.top5.map((r, i) => <RetoCard key={r.name} r={r} i={i} />)}
          </div>
        </section>

        <div className="glass-card p-6 max-w-2xl mb-10">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-white/40 mb-3">{t.linksTitle}</h3>
          <div className="flex flex-col gap-2.5 text-sm">
            <Link to={t.strategyPath} className="hover:text-[#8DC21F] transition-colors">{t.linkStrategy}</Link>
            <Link to={t.projectsPath} className="hover:text-[#8DC21F] transition-colors">{t.linkProjects}</Link>
          </div>
        </div>

        <Link to={t.contactPath} className="btn-primary inline-flex items-center gap-2" data-testid="retos-cta-contact">
          {t.cta} <ArrowRight size={15} />
        </Link>
      </div>
    </>
  );
}
